import { cn } from "@/lib/utils";
import { CompanyLogo } from "./company-logo";
import {
  presentQuizClaimCard,
  type ClaimProofTone,
  type ClaimStatusTone,
} from "../domain/claim-card-display";
import type { SettlementListItem } from "../types";
import proofIcon from "../assets/icon-claim-proof.svg";
import proofRequiredIcon from "../assets/icon-claim-proof-required.svg";
import deadlineIcon from "../assets/icon-claim-deadline.svg";
import customersIcon from "../assets/icon-claim-customers.svg";
import selectIcon from "../assets/icon-claim-select.svg";
import selectedIcon from "../assets/icon-claim-selected.svg";

interface ClaimCardProps {
  item: SettlementListItem;
  selected: boolean;
  onToggle: () => void;
}

const STATUS_CLASS: Record<
  ClaimStatusTone,
  { text: string; bg: string; dot: string }
> = {
  active: { text: "text-mf-green", bg: "bg-[#e6f7ec]", dot: "bg-mf-green" },
  pending: { text: "text-[#d8501f]", bg: "bg-[#fceee8]", dot: "bg-[#d8501f]" },
  potential: {
    text: "text-mf-neutral-400",
    bg: "bg-mf-neutral-100",
    dot: "bg-mf-neutral-400",
  },
};

const PROOF_CLASS: Record<ClaimProofTone, { text: string; icon: string }> = {
  required: { text: "font-medium text-[#d86c1f]", icon: proofRequiredIcon },
  positive: { text: "font-medium text-mf-green", icon: proofIcon },
  unknown: { text: "font-medium text-mf-neutral-400", icon: proofIcon },
};

function DetailRow({
  icon,
  text,
  textClass,
}: {
  icon: string;
  text: string;
  textClass: string;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span className="flex size-4 shrink-0 items-center justify-center overflow-clip">
        <img src={icon} alt="" className="size-full object-contain" />
      </span>
      <span className={cn("truncate text-sm leading-4", textClass)}>
        {text}
      </span>
    </span>
  );
}

export function ClaimCard({ item, selected, onToggle }: ClaimCardProps) {
  const view = presentQuizClaimCard(item);
  const status = STATUS_CLASS[view.statusTone];
  const proof = PROOF_CLASS[view.proofTone];

  return (
    <article
      className={cn(
        "flex w-full flex-col gap-3 overflow-hidden rounded-2xl bg-white px-1 pb-1 pt-4",
        selected ? "border border-mf-green" : "border border-transparent",
      )}
    >
      <div className="flex flex-col gap-3 px-3">
        <div className="flex items-center gap-3">
          <CompanyLogo name={view.company} src={view.iconUrl} size={44} />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="truncate text-base font-bold leading-5 text-mf-neutral-900">
              {view.company}
            </p>
            <span
              className={cn(
                "inline-flex w-fit items-center gap-1 rounded-full px-2 py-1",
                status.bg,
              )}
            >
              <span className={cn("size-1.5 rounded-full", status.dot)} />
              <span
                className={cn(
                  "text-[11px] font-medium leading-[14px]",
                  status.text,
                )}
              >
                {view.statusTitle}
              </span>
            </span>
          </div>
        </div>
        <div className="h-px w-full bg-mf-neutral-200" />
        <div className="flex w-full flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <DetailRow
              icon={proof.icon}
              text={view.proof}
              textClass={proof.text}
            />
            <DetailRow
              icon={deadlineIcon}
              text={view.deadline}
              textClass="text-mf-neutral-900"
            />
          </div>
          <DetailRow
            icon={customersIcon}
            text={view.audience}
            textClass="text-mf-neutral-900"
          />
        </div>
      </div>

      <div
        className={cn(
          "flex items-center justify-between gap-2 rounded-xl p-3",
          selected ? "bg-[#e6f7ec]" : "bg-mf-neutral-50",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <p
            className={cn(
              "text-[11px] leading-[14px]",
              selected ? "text-mf-green" : "text-mf-neutral-400",
            )}
          >
            {view.payoutTitle}
          </p>
          <p className="font-display text-[22px] font-bold leading-[26px] break-words text-mf-neutral-900">
            {view.payoutLabel}
          </p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            "flex h-10 w-[120px] shrink-0 items-center justify-center gap-1 rounded-full text-sm font-bold",
            selected
              ? "bg-mf-green text-white"
              : "bg-white text-mf-neutral-900",
          )}
        >
          <span className="flex size-5 shrink-0 items-center justify-center">
            <img
              src={selected ? selectedIcon : selectIcon}
              alt=""
              className={selected ? "size-[15px]" : "size-[14px]"}
            />
          </span>
          {selected ? "Selected" : "Select"}
        </button>
      </div>
    </article>
  );
}
